import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import CourseProp from '../propTypes/courseProp';
import {
  LESSON_STATUS,
  LESSON_TYPE,
  isAwaitingPaymentConfirmation,
  resolveLessonMediaPayload,
} from '../../../services/utilityFunctions';
import LoggedUserContext from '../../../contextProviders/LoggedUserContext';
import translate from '../../../commonComponents/translate';
import YoutubePlayer from '../../../commonComponents/fileViewers/YoutubePlayer';
import VideoPlayer from '../../../commonComponents/fileViewers/VideoPlayer';
import QuillPdf from '../../../commonComponents/fileViewers/QuillPdf';
import ImageGallery from '../../../commonComponents/fileViewers/ImageGallery';
import WebinarPlayer from '../../../commonComponents/fileViewers/WebinarPlayer';
import ZoomEmbeddedPlayer from '../../../commonComponents/fileViewers/ZoomEmbeddedPlayer';
import QuizDisplay from './QuizDisplay';
import EnrollButton from '../EnrollButton';
import SurveyDisplay from './SurveyDisplay';
import FullScreenBtn from '../../../commonComponents/buttons/FullScreenBtn';
import LessonDocuments from './LessonDocuments';
import SkipStepButton from '../../journey/overall/content/components/SkipStepButton';

function LessonContent(props) {
  const {
    t,
    course,
    selectedLesson,
    onLessonDone,
    gameId,
    showEnrollOverlay,
    onEnroll,
    startPlaying,
    pspdfkitLicenseKey,
    sessionId,
    sessionTrainer,
    onSkipStep,
  } = props;
  const { loggedUser } = useContext(LoggedUserContext);
  const awaitingPayment = isAwaitingPaymentConfirmation(loggedUser, course);
  let currentLesson = {};
  if (selectedLesson) {
    for (const mod of course.modulesList) {
      const tempLesson = mod.lessonsList.find(l => l._id === selectedLesson);
      if (tempLesson) {
        currentLesson = tempLesson;
        break;
      }
    }
  }

  const [lessonIsDone, setLessonIsDone] = useState(false);
  const [videoData, setVideoData] = useState(null); // holds url + type for video lessons
  const handleCompleteLesson = useCallback(() => {
    setLessonIsDone(true);
  }, []);

  // reset state when lesson changes
  useEffect(() => {
    setLessonIsDone(false);
    setVideoData(null);
  }, [selectedLesson]);

  // fetch video link if lesson is VIDEO
  useEffect(() => {
    if (currentLesson?.type === LESSON_TYPE.VIDEO) {
      axios
        .get(
          `/api-media/media/course/${course._id}/lesson/${currentLesson._id}/link`,
        )
        .then(res => {
          // BRAVON-6332 Phase 2b — `/link` answers a bare url string while
          // `CLOUDFRONT_ENABLED` is off and `{url, contentType, expiresAt,
          // kind}` once it is on. Flattened here so the flip is invisible to
          // the player below.
          setVideoData(resolveLessonMediaPayload(res.data));
        })
        .catch(err => {
          console.error('Failed to fetch video link', err);
        });
    }
  }, [course._id, currentLesson]);

  const portletWrapper = (child, customClass = '') => (
    <div
      className="m-portlet m-portlet--rounded"
      style={{ position: 'relative' }}
    >
      <div className={`m-portlet__body course-lesson-container ${customClass}`}>
        {!showEnrollOverlay && child}
        {showEnrollOverlay && (
          <div className="enroll-overlay">
            {/* BRAVON-6462: urging someone to enrol in a course they have
                already ordered reads as if the purchase never happened. */}
            <h2 className="enroll-title">
              {awaitingPayment
                ? t('academy.pendingPayment.title')
                : t('academyDetails_enrollToday')}
            </h2>
            <p className="enroll-message">
              {awaitingPayment
                ? t('academy.pendingPayment.message')
                : t('academyDetails_enrollMessage')}
            </p>
            <EnrollButton onClick={onEnroll} course={course} />
          </div>
        )}
      </div>
    </div>
  );

  const surveyWrapperId = 'surveyWrapper';

  const content = lesson => {
    const showFullScreen = lesson?.surveyContext;
    switch (lesson.type) {
      case LESSON_TYPE.VIDEO: {
        if (!videoData?.url) return <div>Loading video...</div>;
        return portletWrapper(
          <VideoPlayer
            video={{ ...lesson.video, url: videoData.url }}
            contentType={videoData.contentType}
            onVideoFinished={onLessonDone}
            startPlaying={startPlaying}
          />,
        );
      }
      case LESSON_TYPE.YOUTUBE:
        return portletWrapper(
          <div className="video-container">
            <YoutubePlayer
              video={lesson.youtube}
              onVideoFinished={() => onLessonDone(sessionId)}
              startPlaying={startPlaying}
            />
          </div>,
        );
      case LESSON_TYPE.PDF: {
        const PdfComponent = QuillPdf;
        return portletWrapper(
          <PdfComponent
            t={t}
            documentFile={{
              ...lesson.pdf,
              url: `/api-media/media/course/${course._id}/lesson/${lesson._id}`,
            }}
            id="lessonContentPdfViewer"
            onReadingFinished={onLessonDone}
            pspdfkitLicenseKey={pspdfkitLicenseKey}
            isFromCourse
            onCompleteLesson={handleCompleteLesson}
            lessonIsDone={lessonIsDone}
            alreadyCompleted={currentLesson.status === 'done'}
            originalName={lesson.name}
          />,
          'pdf-viewer-wrapper',
        );
      }
      case LESSON_TYPE.IMAGES:
        return portletWrapper(
          <ImageGallery
            images={lesson.images?.map((l, idx) => ({
              ...l,
              url: `/api-media/media/course/${course._id}/lesson/${lesson._id}/image/${idx}`,
              thumb: `/api-media/media/course/${course._id}/lesson/${lesson._id}/image/${idx}`,
            }))}
            onLastImage={onLessonDone}
            onCompleteLesson={handleCompleteLesson}
            lessonIsDone={lessonIsDone}
            alreadyCompleted={currentLesson.status === 'done'}
          />,
        );
      case LESSON_TYPE.QUIZ:
        return portletWrapper(
          <QuizDisplay
            courseId={course._id}
            lessonID={lesson._id}
            questions={lesson.questionsList}
            onQuizDone={onLessonDone}
            gameId={gameId}
          />,
        );
      case LESSON_TYPE.WEBINAR:
        return portletWrapper(
          <WebinarPlayer
            preview={lesson.preview}
            webinar={lesson.webinar}
            title={lesson.tile}
            isDone={lesson.status === LESSON_STATUS.DONE}
            onClickLessonAsDone={onLessonDone}
            isFromCourse
          />,
        );
      case LESSON_TYPE.ZOOM:
        return portletWrapper(<ZoomEmbeddedPlayer webinar={lesson.webinar} />);
      case LESSON_TYPE.SURVEY:
        return portletWrapper(
          <div className="academy-survey-wrapper">
            <SurveyDisplay
              t={t}
              lesson={lesson}
              sessionId={sessionId}
              sessionTrainer={sessionTrainer}
              onLessonDone={onLessonDone}
            />
            {showFullScreen && <FullScreenBtn t={t} id={surveyWrapperId} />}
          </div>,
        );
      default:
        return <div />;
    }
  };

  return (
    <>
      {onSkipStep && (
        <div className="m-portlet m-portlet--rounded skip-wrapper">
          <SkipStepButton onClick={onSkipStep} />
        </div>
      )}
      {content(currentLesson)}
      <LessonDocuments documents={currentLesson?.documents} />
    </>
  );
}

LessonContent.propTypes = {
  t: PropTypes.func.isRequired,
  course: PropTypes.shape(CourseProp.propType).isRequired,
  selectedLesson: PropTypes.string,
  onLessonDone: PropTypes.func.isRequired,
  gameId: PropTypes.string,
  showEnrollOverlay: PropTypes.bool,
  onEnroll: PropTypes.func,
  startPlaying: PropTypes.bool,
  pspdfkitLicenseKey: PropTypes.string,
  sessionId: PropTypes.string,
  sessionTrainer: PropTypes.string,
  onSkipStep: PropTypes.func,
};

LessonContent.defaultProps = {
  selectedLesson: null,
  gameId: null,
  showEnrollOverlay: false,
  onEnroll: null,
  startPlaying: false,
  pspdfkitLicenseKey: null,
  sessionId: null,
  sessionTrainer: null,
  onSkipStep: null,
};

const areEqual = (prevProps, nextProps) =>
  prevProps.course &&
  nextProps.course &&
  prevProps.course._id &&
  nextProps.course._id &&
  prevProps.selectedLesson === nextProps.selectedLesson &&
  prevProps.startPlaying === nextProps.startPlaying &&
  prevProps.onLessonDone === nextProps.onLessonDone;

export default React.memo(translate(LessonContent), areEqual);
